/*
 * UTC days and months, and how the web app and a printed report write them. A day is `YYYY-MM-DD`
 * and a month `YYYY-MM`, both UTC's: Spend's because that is how OpenRouter bills, Insights'
 * because every reporting date is UTC's. A date is written as UTC has it, in the British English
 * every date in the web app is written in ("8 Sept"), so no viewer's zone moves it.
 */

export const dayMs = 86_400_000

/** The first instant of a `YYYY-MM-DD` UTC day, or none when it is not a day of the calendar. */
export function startOfDay(day: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined
  const start = new Date(`${day}T00:00:00Z`)
  // Read back, so a day the calendar has no such date for — the 30th of February — is refused
  // rather than rolled into March.
  return Number.isNaN(start.getTime()) || dayOf(start) !== day ? undefined : start
}

/** The UTC day `at` falls in. */
export function dayOf(at: Date) {
  return at.toISOString().slice(0, 10)
}

/** The first instant of the day after `start`'s. */
export function nextDay(start: Date) {
  return new Date(start.getTime() + dayMs)
}

/** The UTC month `at` falls in: its first instant, the next month's, and its `YYYY-MM`. */
export function monthOf(at: Date) {
  const start = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), 1))
  const end = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 1))
  return { start, end, month: start.toISOString().slice(0, 7) }
}

/*
 * A calendar picks a day in the viewer's own time zone, as a local midnight, and these days are
 * UTC's: the two meet on the calendar date alone, never on an instant, so no zone moves a day.
 */

/** A `YYYY-MM-DD` day as the local midnight a calendar shows it on. */
export function calendarDateOf(day: string) {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number]
  return new Date(year, month - 1, date)
}

/** The `YYYY-MM-DD` day a calendar's pick stands for, from its local date. */
export function dayOfCalendarDate(picked: Date) {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${picked.getFullYear()}-${pad(picked.getMonth() + 1)}-${pad(picked.getDate())}`
}

const utcDate = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" })
const dayMonth = utcDate({ day: "numeric", month: "short" })
const dayMonthYear = utcDate({ day: "numeric", month: "short", year: "numeric" })

/** A `YYYY-MM-DD` UTC day, short, as a chart's axis or a row's date names it: "3 Sept". */
export function dayLabel(day: string) {
  return dayMonth.format(new Date(`${day}T00:00:00Z`))
}

/** A `YYYY-MM-DD` UTC day with its year: "3 Sept 2026". */
export function dayYearLabel(day: string) {
  return dayMonthYear.format(new Date(`${day}T00:00:00Z`))
}

/** A run of days, short: "29–31 Aug" within a month, "30 Aug – 2 Sept" across one. */
export function daysLabel(first: string, last: string) {
  if (first === last) return dayLabel(first)
  const sameMonth = first.slice(0, 7) === last.slice(0, 7)
  return sameMonth
    ? `${Number(first.slice(8))}–${dayLabel(last)}`
    : `${dayLabel(first)} – ${dayLabel(last)}`
}
